"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LevelsRoomScene = void 0;
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
const Scene_1 = require("./Scene");
const pixi_js_1 = require("pixi.js");
const SceneManager_1 = require("../managers/SceneManager");
const settings_1 = require("../config/settings");
const interfaces_1 = require("../../../shared/interfaces");
const ui_1 = require("@pixi/ui");
const Global_1 = require("../Global");
const WigglyText_1 = require("../objects/WigglyText");
const i18next_1 = __importDefault(require("i18next"));
const blocks_1 = require("../blocks");
const LevelSet_1 = require("../blocks/LevelSet");
const levels_1 = require("../../../shared/levels");
const AdManager_1 = require("../managers/AdManager");
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class LevelsRoomScene extends Scene_1.Scene {
    constructor(app, sceneManager, gameManager) {
        super(app, sceneManager, gameManager, settings_1.Settings.levelsColor);
        this.notified = false;
        this.top = 60;
        this.enterterm = null;
        this.levelSets = [];
        this.ticker = new pixi_js_1.Ticker();
        this.musicVolume = settings_1.Settings.loudMusicVolume;
        this.gameManager.leaveRoom();
        this.zIndex = SceneManager_1.Layer.Top;
        this.scrollBox = new ui_1.ScrollBox({
            width: this.scrollSize.width,
            height: this.scrollSize.height,
            disableEasing: false,
            type: 'vertical',
            elementsMargin: (0, Global_1.px)(30),
            bottomPadding: (0, Global_1.px)(50),
            globalScroll: true,
            proximityRange: 100,
            proximityDebounce: 10,
            items: this.makeLevels()
        });
        // Used for hardware enhacement
        this.sigs.push(this.scrollBox.onProximityChange.connect(({ index, inRange }) => {
            this.scrollBox.items[index].alpha = inRange ? 1 : 0;
        }));
        this.subs.add(this.gameManager.room$.subscribe({
            next: (room) => __awaiter(this, void 0, void 0, function* () {
                if (!this.notified && room && room.type === interfaces_1.GameType.Levels && !this.isDeinited) {
                    this.notified = true;
                    this.gameManager.notifyReady(this.enterterm);
                }
            })
        }));
        this.addChild(this.scrollBox);
        this.layout();
        // Save our yspot when we scroll, it's important because the page is so long
        this.ticker.add(() => {
            if (this.scrollBox.scrollY !== -LevelsRoomScene.yspot) {
                LevelsRoomScene.yspot = -this.scrollBox.scrollY;
            }
        });
        this.ticker.start();
    }
    static clearYSpot() {
        LevelsRoomScene.yspot = 0;
    }
    makeLevels() {
        // Label Header
        const style = blocks_1.Block.textStyle(this.overSmallSize ? 36 : 32);
        style.fill = settings_1.Settings.grayColor;
        this.header = new WigglyText_1.WigglyText(this.app, i18next_1.default.t('typeinfo.' + interfaces_1.GameType.Levels), style);
        this.header.anchor.set(0.5, 0);
        const label = new pixi_js_1.Container();
        label.addChild(this.header);
        // Build Levels
        const clevels = levels_1.levels.map((level, lvl) => {
            const selector = (term) => __awaiter(this, void 0, void 0, function* () {
                if (!this.enterterm) {
                    this.enterterm = term;
                    // Trigger ad when selecting an item, first level is free :)
                    AssetManager_1.default.tickSound();
                    if (lvl) {
                        yield this.gameManager.adManager.triggerInterstitial(AdManager_1.AdUnit.GameplayStart);
                    }
                    this.gameManager.startPlay();
                    this.gameManager.enterRoom(interfaces_1.GameType.Levels);
                }
            });
            const ls = new LevelSet_1.LevelSet(this.app, this.gameManager, level, selector, this.cellSize.width);
            this.levelSets.push(ls);
            const container = new pixi_js_1.Container();
            container.addChild(ls);
            container.alpha = 0;
            return container;
        });
        return [label].concat(clevels);
    }
    get scrollSize() {
        return { width: Math.min(this.app.screen.width, (0, Global_1.px)(settings_1.Settings.maxWidth)), height: this.app.screen.height - (0, Global_1.px)(this.isLandscape ? 0 : this.top) };
    }
    get cellSize() {
        return { width: Math.min(this.scrollSize.width, (0, Global_1.px)(settings_1.Settings.maxWidth)), height: 0 };
    }
    exitScene(to, target) {
        const _super = Object.create(null, {
            exitScene: { get: () => super.exitScene }
        });
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // Find our button
            const mybutton = (_a = to.getChildByName('game' + interfaces_1.GameType.Levels, true)) !== null && _a !== void 0 ? _a : to.getChildByName('others', true);
            if (mybutton instanceof ui_1.ButtonContainer) {
                this.zIndex = SceneManager_1.Layer.Top;
                yield this.animateMask(mybutton, true);
            }
            return _super.exitScene.call(this, to);
        });
    }
    enterScene(from, target) {
        const _super = Object.create(null, {
            enterScene: { get: () => super.enterScene }
        });
        return __awaiter(this, void 0, void 0, function* () {
            // Have a cool zoom animation
            if (target instanceof ui_1.ButtonContainer) {
                yield this.animateMask(target);
            }
            return _super.enterScene.call(this, from);
        });
    }
    get triggersStop() {
        return true;
    }
    destroy(_options) {
        this.scrollBox.items.forEach((item) => {
            item.children.forEach((o) => o.destroy(_options));
        });
        this.ticker.destroy();
        super.destroy(_options);
    }
    layout() {
        var _a, _b;
        super.layout();
        this.levelSets.forEach((ls) => {
            ls.w = this.cellSize.width;
            ls.layout();
        });
        this.scrollBox.position.set((0, Global_1.left)(this.app.screen.width), this.isLandscapeBox ? 0 : (0, Global_1.px)(this.top));
        this.scrollBox.width = this.scrollSize.width;
        this.scrollBox.height = this.scrollSize.height;
        this.header.position.set(this.scrollBox.width / 2, (0, Global_1.px)(10));
        const mylevel = (_a = this.gameManager.playerManager.data) === null || _a === void 0 ? void 0 : _a.lvl;
        if (LevelsRoomScene.yspot) {
            this.scrollBox.scrollToPosition({ y: LevelsRoomScene.yspot });
        }
        else if (mylevel && ((_b = this.levelSets[mylevel]) === null || _b === void 0 ? void 0 : _b.parent)) {
            const ypos = Math.max(0, (this.levelSets[mylevel].parent).y - this.scrollBox.height / 3);
            this.scrollBox.scrollToPosition({ y: ypos });
        }
    }
}
exports.LevelsRoomScene = LevelsRoomScene;
LevelsRoomScene.yspot = 0;
